import React, { FunctionComponent, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useFormik } from 'formik';
import * as yup from 'yup';

import Text from 'common/components/Text/Text';
import { SessionState, setUser, logoutUser } from 'features/session/sessionSlice';
import { cleanFavorites, cleanNextSteps } from 'features/user/userSlice';

import { useAppDispatch, useAppSelector } from 'app/hooks';

import styles from './UserInformation.module.scss';
import { AccountSettingsImg, EditIcon } from 'assets/session/dashboard/index';
import defaultIcon from 'assets/explore/icon-default.svg';
import { navigateTop } from 'common/utils';

import { useUpdateUserMutation, useDeleteUserMutation } from 'services/exploreApi';

const UpdateSchema = yup.object().shape({
  password: yup.string(),
  new_password: yup.string().min(8).max(32),
  confirmation: yup.string().when('password', {
    is: (val: string) => (val && val.length > 0 ? true : false),
    then: yup.string().oneOf([yup.ref('new_password')], "Passwords don't match"),
  }),
});

interface UpdateInfo {
  password: string;
  new_password: string;
  confirmation: string;
  app_communication: boolean;
  marketing_communication: boolean;
  photo: any;
}

const AccountSettings: FunctionComponent = () => {
  const { user } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [profilePhoto, setProfilePhoto] = useState<string>(user?.photo ? user?.photo : '');
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();
  const dispatch = useAppDispatch();
  const [deleteUser] = useDeleteUserMutation();
  const fileRef = React.createRef<HTMLInputElement>();
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    formik.setFieldValue('photo', file);
    const reader = new FileReader();
    reader.onload = () => {
      setProfilePhoto(String(reader.result));
    };
  };

  const showOpenFileDialog = () => {
    fileRef?.current?.click();
  };

  const handleDelete = () => {
    deleteUser(parseInt(String(user?.id) || '')).then(() => {
      dispatch(logoutUser());
      dispatch(cleanFavorites());
      dispatch(cleanNextSteps());
      navigate('/');
      navigateTop();
    });
  };

  const formik = useFormik<UpdateInfo>({
    initialValues: {
      password: '',
      new_password: '',
      confirmation: '',
      app_communication: user?.app_communication ? true : false,
      marketing_communication: user?.marketing_communication ? true : false,
      photo: user?.photo,
    },
    validationSchema: UpdateSchema,
    onSubmit: async (updateInfo: UpdateInfo) => {
      if (user) {
        const formData = new FormData();
        formData.append('user[password]', updateInfo.password);
        formData.append('user[new_password]', updateInfo.new_password);
        formData.append('user[password_confirmation]', updateInfo.confirmation);
        formData.append('user[app_communication]', String(updateInfo.app_communication));
        formData.append(
          'user[marketing_communication]',
          String(updateInfo.marketing_communication)
        );
        formData.append('user[photo]', updateInfo.photo);
        await updateUser({ data: formData, id: user?.id })
          .unwrap()
          .then((res) => {
            dispatch(setUser({ ...res.data }));
            navigate('/home');
          });
      }
    },
  });

  return (
    <div className={`${isMobile ? '' : 'container d-flex flex-row'}`}>
      <div className={styles.sectionContainer}>
        <form onSubmit={formik.handleSubmit}>
          {isMobile ? (
            <div className={styles.header}>
              <div className={styles.prevStepIcon} onClick={() => navigate(-1)}>
                <i className="bi bi-chevron-left"></i>
              </div>
              <Text size={18} weight={700} color="primary">
                Account settings
              </Text>
              <img src={AccountSettingsImg} className={styles.image}></img>
            </div>
          ) : (
            <div style={{ marginBottom: '22px' }}>
              <Text size={16} weight={500} color="primary">
                Account {'>'} Settings
              </Text>
              <div className={styles.desktopHeader}>
                <div>
                  <Text size={24} weight={700} color="primary" inlineStyle={{ marginTop: '30px' }}>
                    Account settings
                  </Text>
                  <Text size={18} color="secondary" inlineStyle={{ marginTop: '10px' }}>
                    Set your account setting down below
                  </Text>
                </div>
                <div>
                  <button className={styles.saveButton} type="submit">
                    Save & Leave
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className={styles.stepContainer}>
            <Text
              size={isMobile ? 16 : 20}
              weight={600}
              color="primary"
              inlineStyle={{ marginBottom: '20px' }}
            >
              General
            </Text>
            <div className={styles.profilePhotoContainer}>
              <img src={profilePhoto || defaultIcon} className={styles.photo}></img>
              <div className={styles.editPhotoButton}>
                <input
                  ref={fileRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".jpg, .png"
                  onChange={handleFile}
                  name="photo"
                />
                <img src={EditIcon} onClick={showOpenFileDialog}></img>
              </div>
            </div>
            <div className={styles.inputsContainter} style={{ marginTop: '42px' }}>
              <div className={styles.inputGroup}>
                <Text
                  size={16}
                  weight={isMobile ? 500 : 600}
                  inlineStyle={{ marginBottom: '12px' }}
                  color="primary"
                >
                  Email
                </Text>
                <input className="form-control" type="text" disabled value={user?.email} />
              </div>
              <div className={styles.inputGroup}>
                <button onClick={handleDelete} className={`${styles.deleteButton} btn`}>
                  <Text
                    size={14}
                    weight={isMobile ? 500 : 600}
                    inlineStyle={{ marginBottom: '12px', marginTop: isMobile ? '0' : '45px' }}
                    color="secondary"
                  >
                    Delete this account
                  </Text>
                </button>
              </div>
            </div>
            {!isMobile && <hr />}
            {user?.provider !== 'google_oauth2' && (
              <>
                <Text
                  size={isMobile ? 16 : 20}
                  weight={600}
                  color="primary"
                  inlineStyle={{ marginBottom: '20px', marginTop: isMobile ? '24px' : '40px' }}
                >
                  Password
                </Text>
                <div className={styles.inputsContainter}>
                  <div className={styles.inputGroup}>
                    <Text
                      size={16}
                      weight={isMobile ? 500 : 600}
                      inlineStyle={{ marginBottom: '12px' }}
                      color="primary"
                    >
                      Current password
                    </Text>
                    <input
                      className="form-control"
                      type="password"
                      name="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.password && formik.touched.password && (
                      <p className={styles.warningLabel}>{formik.errors.password}</p>
                    )}
                  </div>
                </div>
                <div className={styles.inputsContainter}>
                  <div className={styles.inputGroup}>
                    <Text
                      size={16}
                      weight={isMobile ? 500 : 600}
                      inlineStyle={{ marginBottom: '12px' }}
                      color="primary"
                    >
                      New password
                    </Text>
                    <input
                      className="form-control"
                      type="password"
                      name="new_password"
                      value={formik.values.new_password}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.confirmation && formik.touched.confirmation && (
                      <p className={styles.warningLabel}>{formik.errors.confirmation}</p>
                    )}
                    {formik.errors.new_password && formik.touched.new_password && (
                      <p className={styles.warningLabel}>{formik.errors.new_password}</p>
                    )}
                  </div>
                  <div className={styles.inputGroup}>
                    <Text
                      size={16}
                      weight={isMobile ? 500 : 600}
                      inlineStyle={{ marginBottom: '12px' }}
                      color="primary"
                    >
                      Confirm password
                    </Text>
                    <input
                      className="form-control"
                      type="password"
                      name="confirmation"
                      value={formik.values.confirmation}
                      onChange={formik.handleChange}
                    />
                    {formik.errors.confirmation && formik.touched.confirmation && (
                      <p className={styles.warningLabel}>{formik.errors.confirmation}</p>
                    )}
                  </div>
                </div>
              </>
            )}
            {!isMobile && <hr />}
            <Text
              size={isMobile ? 16 : 20}
              weight={600}
              color="primary"
              inlineStyle={{ marginTop: isMobile ? '24px' : '40px' }}
            >
              Notifications
            </Text>
            <div className={styles.notificationsContainer}>
              <div className={styles.notificationsGroup}>
                <Text size={16} weight={500} color="primary">
                  Email Notifications
                </Text>
                <div className={styles.notification}>
                  <Text size={14} weight={isMobile ? 400 : 500} color="primary">
                    Application related communication
                  </Text>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      name="app_communication"
                      checked={formik.values.app_communication}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
                {/* <Text size={14} color="secondary">
                  Lorem ipsum dolor sit amet, consectetur adipiscin consectetur adipiscin
                </Text> */}
                {/* <div className={styles.notification}>
                  <Text size={14} weight={isMobile ? 400 : 500} color="primary">
                    Newsletter
                  </Text>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch" />
                  </div>
                </div>
                <Text size={14} color="secondary">
                  Lorem ipsum dolor sit amet, consectetur adipiscin consectetur adipiscin
                </Text> */}
                <div className={styles.notification}>
                  <Text size={14} weight={isMobile ? 400 : 500} color="primary">
                    Marketing Communication
                  </Text>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      name="marketing_communication"
                      checked={formik.values.marketing_communication}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
                {/* <Text size={14} color="secondary">
                  Lorem ipsum dolor sit amet, consectetur adipiscin consectetur adipiscin
                </Text> */}
              </div>
              {/* <div className={styles.notificationsGroup}>
                <Text size={16} weight={500} color="primary">
                  Whatsapp Notifications
                </Text>
                <div className={styles.notification}>
                  <Text size={14} weight={isMobile ? 400 : 500} color="primary">
                    Application related communication
                  </Text>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" role="switch" />
                  </div>
                </div>
                <Text size={14} color="secondary">
                  Lorem ipsum dolor sit amet, consectetur adipiscin consectetur adipiscin
                </Text>
              </div> */}
            </div>
            {isMobile && (
              <>
                <button
                  className={styles.nextStepButton}
                  type="submit"
                  onClick={() => formik.handleSubmit()}
                >
                  Finish
                </button>
                <button className={styles.saveButton} onClick={() => null}>
                  Save & Leave
                </button>
              </>
            )}
          </div>
        </form>
      </div>
      {!isMobile && <div className={styles.rightColumn}></div>}
    </div>
  );
};

export default AccountSettings;
